import type { SVGAttributes } from 'react';

import { cn } from '@shared/lib/cn';

import {
  type FileTypeIconName,
  type GeneralIconName,
  ICON_SIZE_CLASS,
  ICON_STROKE_WIDTH,
  type IconSize,
  type StatIconName,
} from './icon.constant';
import { ICON_GLYPHS } from './icon.glyphs';

interface IconBaseProps extends Omit<SVGAttributes<SVGSVGElement>, 'children' | 'name'> {
  /**
   * 뜻을 가진 아이콘일 때 보조기기가 읽을 이름. 주면 `role="img"` + `aria-label`, 없으면 장식(`aria-hidden`).
   * 아이콘만 있는 버튼은 여기가 아니라 버튼 쪽에 `aria-label` 을 단다
   */
  label?: string;
}

/**
 * 이름에 따라 쓸 수 있는 크기가 다르다(DD-17 I3 · I8).
 * - stat-* : lg(24) 이상 — 통계 카드 전용
 * - file-doc · xls · ppt · pdf · img · etc : md(20) 이상 — 글자 라벨이 있다
 * - 그 밖: xs ~ xl. 이름이 없으면 크기만 같은 빈 자리(아이콘을 정하지 않은 자리)
 */
type IconNameSize =
  | { name: StatIconName; size: 'lg' | 'xl' }
  | { name: FileTypeIconName; size: 'md' | 'lg' | 'xl' }
  | { name?: GeneralIconName; size?: IconSize };

export type IconProps = IconBaseProps & IconNameSize;

/**
 * 아이콘(Figma `Icon/*` 73개, 생성물 `icon.glyphs.ts`). viewBox 24 의 인라인 svg.
 * 색은 `currentColor` 를 따른다(부모의 `text-*`). 선 굵기는 크기와 상관없이 화면에서 1.5px
 * (`vector-effect: non-scaling-stroke`) — Figma 인스턴스를 줄이고 키울 때와 같다.
 * 예외: glyph 에 `strokeWidth` 가 있는 아이콘(plus-thin 0.75, DD-23 3)은 그 굵기.
 */
export function Icon(props: IconProps) {
  const { name, size = 'xs', label, className, ...rest } = props;
  const glyph = name ? ICON_GLYPHS[name] : undefined;
  const a11y = label
    ? ({ role: 'img', 'aria-label': label, 'aria-hidden': undefined } as const)
    : ({ 'aria-hidden': true } as const);
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      focusable="false"
      {...rest}
      {...a11y}
      data-icon={name}
      data-size={size}
      className={cn('inline-block shrink-0', ICON_SIZE_CLASS[size], className)}
    >
      {glyph?.map((path, index) =>
        path.paint === 'stroke' ? (
          <path
            key={index}
            d={path.d}
            stroke="currentColor"
            strokeWidth={path.strokeWidth ?? ICON_STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ) : (
          <path key={index} d={path.d} fill="currentColor" fillRule={path.fillRule} />
        ),
      )}
    </svg>
  );
}
